import { Link } from 'react-router-dom'
import { CircleSlash } from 'lucide-react'
import type { CategoryBudget } from '@/api/budget'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { perDay, sortWorstFirst } from '@/lib/budgetPace'
import { CategoryIcon } from '@/lib/categoryIcon'
import { fromPaise, toPaise } from '@/lib/splitMath'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { TodayTick } from './BudgetHero'

export const PANEL =
  'rounded-2xl border border-[var(--fl-border)] bg-[var(--fl-surface)] px-4 pb-2 md:px-5'
export const ICON_TILE =
  'flex h-7 w-7 shrink-0 items-center justify-center rounded-[9px] bg-[var(--fl-surface-subtle)] text-[var(--fl-text-2)]'

function barColor(cat: CategoryBudget): string {
  if (cat.isOverBudget) return 'bg-[var(--fl-error)]'
  if (cat.percentUsed >= 75) return 'bg-[var(--fl-warning-text)]'
  return 'bg-[var(--fl-hero-bar)]'
}

function MonthlyRow({
  cat,
  href,
  progress,
}: {
  cat: CategoryBudget
  href: string
  progress: { isCurrent: boolean; elapsedFraction: number; daysLeft: number }
}) {
  const { formatWhole } = useFormatCurrency()
  const left = fromPaise(toPaise(cat.limit) - toPaise(cat.spent))
  const daily = progress.isCurrent ? perDay(left, progress.daysLeft) : null
  const note = cat.isOverBudget
    ? `${formatWhole(-left)} over`
    : left === 0
      ? 'Fully used'
      : [`${formatWhole(left)} left`, daily != null ? `${formatWhole(daily)} a day` : null]
          .filter(Boolean)
          .join(' · ')

  return (
    <li className="py-3">
      <div className="flex items-center gap-2.5 text-sm">
        <CategoryIcon icon={cat.icon} size={13} className={ICON_TILE} />
        <Link
          to={href}
          className="min-w-0 flex-1 truncate font-medium text-[var(--fl-text-1)] hover:underline"
        >
          {cat.name}
        </Link>
        <span
          className={cn(
            'shrink-0 font-semibold tabular-nums',
            cat.isOverBudget ? 'text-[var(--fl-error)]' : 'text-[var(--fl-text-1)]'
          )}
        >
          {formatWhole(cat.spent)}
          <span className="font-normal text-[var(--fl-text-2)]"> / {formatWhole(cat.limit)}</span>
        </span>
      </div>
      <div className="relative ml-[38px] mt-2 h-[7px] rounded-full bg-[var(--fl-surface-subtle)]">
        <div
          className={cn('h-full rounded-full', barColor(cat))}
          style={{ width: `${Math.min(cat.percentUsed, 100)}%` }}
        />
        {progress.isCurrent && <TodayTick fraction={progress.elapsedFraction} />}
      </div>
      <div className="ml-[38px] mt-1.5 flex items-center justify-between gap-2 text-xs text-[var(--fl-text-2)]">
        <span className={cn('tabular-nums', cat.isOverBudget && 'text-[var(--fl-error)]')}>
          {note}
        </span>
        {cat.isOverBudget ? (
          <Badge tone="error">Over</Badge>
        ) : cat.percentUsed >= 75 ? (
          <Badge tone="warning">{Math.round(cat.percentUsed)}%</Badge>
        ) : null}
      </div>
    </li>
  )
}

export function MonthlyBudgets({
  categories,
  unbudgetedSpend,
  heading,
  progress,
  hrefFor,
  unbudgetedHref,
}: {
  categories: CategoryBudget[]
  unbudgetedSpend: number
  /** The period, e.g. "October" — the panel shows monthly budgets scaled to it. */
  heading: string
  progress: { isCurrent: boolean; elapsedFraction: number; daysLeft: number }
  hrefFor: (categoryId: string) => string
  unbudgetedHref: string
}) {
  const { formatWhole } = useFormatCurrency()
  return (
    <section aria-label="Monthly budgets" className={PANEL}>
      <div className="flex items-baseline justify-between gap-3 pb-1 pt-3.5">
        <h2 className="text-[15px] font-semibold text-[var(--fl-text-1)]">Monthly budgets</h2>
        <span className="text-xs text-[var(--fl-text-2)]">{heading}</span>
      </div>
      {categories.length === 0 ? (
        <p className="py-3 text-sm text-[var(--fl-text-2)]">No monthly budgets set.</p>
      ) : (
        <ul className="divide-y divide-[var(--fl-divider)]">
          {sortWorstFirst(categories).map((cat) => (
            <MonthlyRow
              key={cat.categoryId}
              cat={cat}
              href={hrefFor(cat.categoryId)}
              progress={progress}
            />
          ))}
        </ul>
      )}
      {unbudgetedSpend > 0 && (
        <Link
          to={unbudgetedHref}
          className="-mx-4 -mb-2 mt-1 flex items-center gap-2.5 rounded-b-2xl border-t border-[var(--fl-divider)] bg-[var(--fl-surface-hover)] px-4 py-3 text-sm hover:bg-[var(--fl-surface-subtle)] md:-mx-5 md:px-5"
        >
          <span className={ICON_TILE}>
            <CircleSlash size={13} aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1 font-medium text-[var(--fl-text-1)]">
            Not budgeted
            <span className="ml-1.5 text-xs font-normal text-[var(--fl-text-2)]">
              categories with no budget, and uncategorised
            </span>
          </span>
          <span className="shrink-0 font-semibold tabular-nums text-[var(--fl-text-1)]">
            {formatWhole(unbudgetedSpend)}
          </span>
        </Link>
      )}
    </section>
  )
}
